import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StudioContext = createContext(null);

export const useStudio = () => {
  const context = useContext(StudioContext);
  if (!context) {
    throw new Error('useStudio must be used within a StudioProvider');
  }
  return context;
};

export const StudioProvider = ({ children }) => {
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStudios = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/studios`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const studioList = response.data || [];
      setStudios(studioList);

      // Get saved studio from localStorage or use default
      const savedStudioId = localStorage.getItem('selectedStudioId');

      // Iter 287: „Alle Studios" entfernt — wenn ein alter Sentinel-Wert
      // gespeichert ist, ignorieren wir ihn und springen auf das
      // Default-Studio (i.d.R. Wetzikon).
      const validSaved = savedStudioId && savedStudioId !== '__all__'
        ? studioList.find(s => s.id === savedStudioId)
        : null;
      if (validSaved) {
        setSelectedStudio(validSaved);
      } else if (studioList.length > 0) {
        const defaultStudio = studioList.find(s => s.is_default) || studioList[0];
        setSelectedStudio(defaultStudio);
        localStorage.setItem('selectedStudioId', defaultStudio.id);
      }
    } catch (error) {
      console.error('Error fetching studios:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch studios on mount and when token changes
  useEffect(() => {
    fetchStudios();
    
    // Also listen for login events to refresh studios
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        fetchStudios();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Check for token periodically after mount (for initial login)
    const checkToken = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && studios.length === 0) {
        fetchStudios();
      }
    }, 1000);
    
    // Clear interval after 5 seconds
    setTimeout(() => clearInterval(checkToken), 5000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkToken);
    };
  }, [fetchStudios, studios.length]);

  const selectStudio = (studio) => {
    setSelectedStudio(studio);
    localStorage.setItem('selectedStudioId', studio.id);
    // Trigger page refresh to reload data with new studio
    window.dispatchEvent(new CustomEvent('studioChanged', { detail: studio }));
  };

  const value = {
    studios,
    selectedStudio,
    selectedStudioId: selectedStudio?.id || null,
    selectStudio,
    loading,
    refreshStudios: fetchStudios
  };

  return (
    <StudioContext.Provider value={value}>
      {children}
    </StudioContext.Provider>
  );
};

export default StudioContext;
